import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TurmaCatequizandoDto } from '../../../core/api/turma-catequizando.dto';
import { UsuarioTurmaVinculoDto } from '../../../core/api/usuario-turma-vinculo.dto';
import { AuthService } from '../../../core/auth/auth.service';
import { Pessoa } from '../../../core/api/pessoa';
import { Turma } from '../../../core/api/turma';
import { PessoaFormComponent } from '../../pessoa/pessoa-form/pessoa-form.component';
import { PessoaService } from '../../../core/api/pessoa.service';
import { TurmaCatequizandoService } from '../../../core/api/turma-catequizando.service';
import { TurmaService } from '../../../core/api/turma.service';
import { DialogAlertComponent } from '../../inscricao/inscricao.dialog.component';
import { TransferirDialogComponent } from '../transferir-dialog/transferir-dialog.component';
import { TurmaFormComponent } from '../turma-form/turma-form.component';

import { mensagemDeErro } from '../../../utils/mensagem-erro';
@Component({
  selector: 'app-turma-detalhes-dialog',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbIconModule, NbSpinnerModule],
  templateUrl: './turma-detalhes-dialog.component.html',
  styleUrls: ['./turma-detalhes-dialog.component.scss'],
})
export class TurmaDetalhesDialogComponent implements OnInit {
  @Input({ required: true }) turma!: Turma;

  turmaCabecalho: Turma = new Turma();
  carregando = true;
  matriculas: TurmaCatequizandoDto[] = [];
  usuarios: UsuarioTurmaVinculoDto[] = [];
  erroAlunos: string | null = null;
  erroUsuarios: string | null = null;
  private houveAlteracao = false;

  fotoPopupAberta = false;
  fotoPopupUrl: string | null = null;
  fotoPopupTitulo = '';
  fotoPopupDocumentoId: string | null = null;

  constructor(
    private turmaService: TurmaService,
    private matriculaService: TurmaCatequizandoService,
    private pessoaService: PessoaService,
    private auth: AuthService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    protected ref: NbDialogRef<TurmaDetalhesDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.turmaCabecalho = new Turma(this.turma);
    const id = this.turmaCabecalho.id?.trim();
    if (!id) {
      this.carregando = false;
      this.erroAlunos = 'Turma sem identificador.';
      return;
    }
    this.recarregarListas();
  }

  isAtiva(): boolean {
    return this.turmaCabecalho.ativo !== false;
  }

  abrirEditar(): void {
    const id = this.turmaCabecalho.id?.trim();
    if (!id) return;
    this.dialogService
      .open(TurmaFormComponent, { context: { turma: new Turma(this.turmaCabecalho) } })
      .onClose.subscribe((ok) => {
        if (ok) {
          this.houveAlteracao = true;
          this.recarregarListas();
        }
      });
  }

  fechar(): void {
    this.ref.close(this.houveAlteracao);
  }

  trackByMatricula = (_: number, m: TurmaCatequizandoDto): string =>
    m.id ?? m.pessoaId ?? String(_);
  trackByUsuario = (_: number, u: UsuarioTurmaVinculoDto): string => u.id ?? u.login ?? String(_);

  /** Admin ou coordenação (coordenador): pode abrir o cadastro da pessoa. */
  podeLinkCadastroPessoa(): boolean {
    const p = this.auth.getPerfil();
    return !!p && (!!p.admin || !!p.coordenacao);
  }

  abrirCadastroPessoa(m: TurmaCatequizandoDto, ev?: Event): void {
    ev?.preventDefault();
    if (!this.podeLinkCadastroPessoa()) return;
    const id = m.pessoaId?.trim();
    if (!id) {
      this.toastr.warning('Pessoa sem identificador.', 'Cadastro');
      return;
    }
    this.pessoaService.buscarPorId(id).subscribe({
      next: (pes) => {
        this.dialogService
          .open(PessoaFormComponent, { context: { pessoa: new Pessoa(pes) } })
          .onClose.subscribe((ok) => {
            if (ok) {
              this.houveAlteracao = true;
              this.recarregarListas();
            }
          });
      },
      error: (err) => {
        this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar o cadastro da pessoa.'), 'Erro');
      },
    });
  }

  temFoto(m: TurmaCatequizandoDto): boolean {
    const v = m.fotoDocumentoId;
    return v != null && String(v).trim() !== '';
  }

  verFoto(m: TurmaCatequizandoDto, ev?: Event): void {
    ev?.stopPropagation();
    if (!this.temFoto(m) || !m.pessoaId) return;
    const docId = m.fotoDocumentoId?.trim();
    if (!docId) return;
    this.fotoPopupTitulo = m.pessoaNome ?? 'Foto';
    this.fotoPopupDocumentoId = docId;
    const base = this.pessoaService.urlFotoPessoa(m.pessoaId);
    this.fotoPopupUrl = `${base}?t=${Date.now()}&fotoDocumentoId=${encodeURIComponent(docId)}`;
    this.fotoPopupAberta = true;
  }

  fecharFotoPopup(): void {
    this.fotoPopupAberta = false;
    this.fotoPopupUrl = null;
    this.fotoPopupTitulo = '';
    this.fotoPopupDocumentoId = null;
  }

  onFotoPopupErro(): void {
    this.toastr.danger('Não foi possível carregar a foto.', 'Erro');
    this.fecharFotoPopup();
  }

  transferir(m: TurmaCatequizandoDto, ev?: Event): void {
    ev?.stopPropagation();
    this.dialogService
      .open(TransferirDialogComponent, { context: { matricula: m } })
      .onClose.subscribe((ok) => {
        if (ok) {
          this.toastr.success('Transferência concluída.', 'OK');
          this.houveAlteracao = true;
          this.recarregarListas();
        }
      });
  }

  desmatricular(m: TurmaCatequizandoDto, ev?: Event): void {
    ev?.stopPropagation();
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Desmatricular',
          message: `Remover ${m.pessoaNome} da turma ${m.turmaNome}?`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, desmatricular',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.matriculaService.desmatricular(m.turmaId, m.pessoaId).subscribe({
          next: () => {
            this.toastr.success(`${m.pessoaNome} saiu desta turma.`, 'Matrícula');
            this.houveAlteracao = true;
            this.recarregarListas();
          },
          error: (err) => {
            const msg = mensagemDeErro(err, 'Não foi possível desmatricular.');
            this.toastr.danger(msg, 'Erro');
          },
        });
      });
  }

  private recarregarListas(): void {
    const turmaId = this.turmaCabecalho.id?.trim();
    if (!turmaId) return;
    this.erroAlunos = null;
    this.erroUsuarios = null;
    this.carregando = true;
    forkJoin({
      turma: this.turmaService.buscarPorId(turmaId).pipe(
        catchError(() => of<Turma | null>(null)),
      ),
      alunos: this.matriculaService.listar(turmaId).pipe(
        catchError((err) => {
          this.erroAlunos = mensagemDeErro(err, 'Não foi possível carregar os catequizandos.');
          return of<TurmaCatequizandoDto[]>([]);
        }),
      ),
      usuarios: this.turmaService.listarUsuariosTurma(turmaId).pipe(
        catchError((err) => {
          this.erroUsuarios = mensagemDeErro(err, 'Não foi possível carregar os usuários com acesso à turma.');
          return of<UsuarioTurmaVinculoDto[]>([]);
        }),
      ),
    })
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe(({ turma, alunos, usuarios }) => {
        if (turma) {
          this.turmaCabecalho = new Turma(turma);
        }
        this.matriculas = Array.isArray(alunos) ? alunos : [];
        this.usuarios = Array.isArray(usuarios) ? usuarios : [];
      });
  }
}
